import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const emptyReport = {
  summary: null,
  sizeColumns: [],
  columnTotals: {},
  mapped: [],
  unmapped: [],
};

const initialState = {
  loading: false,
  saving: false,
  historyLoading: false,
  // Result of a parse that has not been saved yet.
  preview: null,
  // A saved import opened from the history table.
  detail: null,
  history: { list: [], total: 0, perPage: "" },
  // The stock out plan for the import currently open, and whether a stock out
  // or undo is in flight.
  stockOut: { loading: false, working: false, data: null },
  // The report PDF is built on the server; this is only the wait for it.
  pdfLoading: false,
};

export const parseLabelPdf = createAsyncThunk(
  "order-label/parse",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("order-label/parse", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const saveLabelImport = createAsyncThunk(
  "order-label/save",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("order-label/save", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const discardLabelImport = createAsyncThunk(
  "order-label/discard",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("order-label/discard", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getLabelImportHistory = createAsyncThunk(
  "order-label/history",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("order-label/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const updateImportQuantities = createAsyncThunk(
  "order-label/update-quantities",
  async ({ id, overrides }, { dispatch }) => {
    try {
      const response = await axios.put(
        `order-label/import-history/${id}/quantities`,
        { overrides }
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getLabelImportById = createAsyncThunk(
  "order-label/history-detail",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`order-label/import-history/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getStockOutPlan = createAsyncThunk(
  "order-label/stock-out-plan",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(
        `order-label/import-history/${id}/stock-out`
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

/** `date` (YYYY-MM-DD) is the day the stock leaves; left out, it is today. */
export const runStockOut = createAsyncThunk(
  "order-label/stock-out",
  async ({ id, date }, { dispatch }) => {
    try {
      const response = await axios.post(
        `order-label/import-history/${id}/stock-out`,
        { date }
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

/**
 * Without `recordIds` the whole import goes back; with them, only those
 * variants are reverted.
 */
export const undoStockOut = createAsyncThunk(
  "order-label/undo-stock-out",
  async ({ id, recordIds }, { dispatch }) => {
    try {
      const response = await axios.post(
        `order-label/import-history/${id}/stock-out/undo`,
        recordIds ? { recordIds } : {}
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteLabelImport = createAsyncThunk(
  "order-label/delete",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`order-label/import-history/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const downloadImportPdf = createAsyncThunk(
  "order-label/pdf",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`order-label/import-history/${id}/pdf`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

const orderLabelSlice = createSlice({
  name: "orderLabel",
  initialState,
  reducers: {
    clearPreview: (state) => {
      state.preview = null;
    },
    clearDetail: (state) => {
      state.detail = null;
    },
    clearStockOut: (state) => {
      state.stockOut = { loading: false, working: false, data: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(parseLabelPdf.pending, (state) => {
        state.loading = true;
        state.preview = null;
      })
      .addCase(parseLabelPdf.fulfilled, (state, action) => {
        state.loading = false;
        const data = action?.payload?.data?.data;
        state.preview = data?.parseToken ? { ...emptyReport, ...data } : null;
      })
      .addCase(parseLabelPdf.rejected, (state) => {
        state.loading = false;
        state.preview = null;
      });

    builder
      .addCase(saveLabelImport.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveLabelImport.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveLabelImport.rejected, (state) => {
        state.saving = false;
      });

    builder
      .addCase(updateImportQuantities.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateImportQuantities.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateImportQuantities.rejected, (state) => {
        state.saving = false;
      });

    builder
      .addCase(getLabelImportHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(getLabelImportHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        const data = action?.payload?.data?.data;
        state.history = {
          list: data?.list ?? [],
          total: data?.total ?? 0,
          perPage: data?.perPage ?? "",
        };
      })
      .addCase(getLabelImportHistory.rejected, (state) => {
        state.historyLoading = false;
        state.history = { list: [], total: 0, perPage: "" };
      });

    builder
      .addCase(getLabelImportById.pending, (state) => {
        state.loading = true;
        state.detail = null;
      })
      .addCase(getLabelImportById.fulfilled, (state, action) => {
        state.loading = false;
        const data = action?.payload?.data?.data;
        state.detail = data?.history ? { ...emptyReport, ...data } : null;
      })
      .addCase(getLabelImportById.rejected, (state) => {
        state.loading = false;
        state.detail = null;
      });

    builder
      .addCase(downloadImportPdf.pending, (state) => {
        state.pdfLoading = true;
      })
      .addCase(downloadImportPdf.fulfilled, (state) => {
        state.pdfLoading = false;
      })
      .addCase(downloadImportPdf.rejected, (state) => {
        state.pdfLoading = false;
      });

    builder
      .addCase(getStockOutPlan.pending, (state) => {
        state.stockOut.loading = true;
        // Dropped up front so a plan from the previous import can never be on
        // screen while this one loads - the buttons act on the new import.
        state.stockOut.data = null;
      })
      .addCase(getStockOutPlan.fulfilled, (state, action) => {
        state.stockOut.loading = false;
        state.stockOut.data = action?.payload?.data?.data ?? null;
      })
      .addCase(getStockOutPlan.rejected, (state) => {
        state.stockOut.loading = false;
        state.stockOut.data = null;
      });

    // Both writes share one flag: the panel disables every button while
    // either is running, so the two can never overlap.
    builder
      .addCase(runStockOut.pending, (state) => {
        state.stockOut.working = true;
      })
      .addCase(runStockOut.fulfilled, (state) => {
        state.stockOut.working = false;
      })
      .addCase(runStockOut.rejected, (state) => {
        state.stockOut.working = false;
      });

    builder
      .addCase(undoStockOut.pending, (state) => {
        state.stockOut.working = true;
      })
      .addCase(undoStockOut.fulfilled, (state) => {
        state.stockOut.working = false;
      })
      .addCase(undoStockOut.rejected, (state) => {
        state.stockOut.working = false;
      });
  },
});

export const { clearPreview, clearDetail, clearStockOut } =
  orderLabelSlice.actions;
export default orderLabelSlice.reducer;
